import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { EmployerFootprintAdminViewDto, EmployerVerificationDetail } from '../../api/employerVerificationDashboardApi';
import { findingBriefElementId } from '../../lib/employerReview/employerReviewNavigate';
import {
  findingSeverityChipProps,
  findingVisualTheme,
} from '../../lib/employerReview/findingVisualTheme';
import { resolveAdjudicatorFindingBriefs } from '../../lib/employerReview/resolveAdjudicatorFindingBrief';
import type { AdjudicatorFindingBrief } from '../../lib/employerReview/resolveAdjudicatorFindingBrief';
import type {
  EmployerConflictInfo,
  ReviewReason,
} from '../../lib/employerReview/reviewReason.types';
import { AdjudicatorFindingBriefPanel } from './AdjudicatorFindingBriefPanel';
import {
  concernAccordionDetailsSx,
  concernAccordionShellSx,
  concernAccordionSummarySx,
  concernIndexBadgeSx,
  resolveConcernAccent,
  severityChipSx,
} from './concernAccordionStyles';
import {
  FINDING_BRIEF_SECTION_LABEL,
  verificationBriefIntro,
} from './findingBriefSectionStyles';
import { useEmployerEvidenceTourOptional } from './guidedTour/EmployerEvidenceTourContext';

function FindingBriefAccordion({
  brief,
  index,
  expanded,
  onToggle,
}: {
  brief: AdjudicatorFindingBrief;
  index: number;
  expanded: boolean;
  onToggle: (open: boolean) => void;
}) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const visual = findingVisualTheme(brief.findingId, brief.severity, isLight);
  const chip = findingSeverityChipProps(brief.findingId, brief.severity, isLight);
  const accent = resolveConcernAccent(visual, brief.severity, theme, isLight);

  return (
    <Accordion
      id={findingBriefElementId(brief.findingId)}
      expanded={expanded}
      onChange={(_, open) => onToggle(open)}
      disableGutters
      elevation={0}
      sx={concernAccordionShellSx(accent)}
      data-tour-target={findingBriefElementId(brief.findingId)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon sx={{ fontSize: 22 }} />}
        sx={concernAccordionSummarySx(accent)}
      >
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', width: '100%', pr: 0.5 }}>
          <Box component="span" sx={concernIndexBadgeSx(accent)}>
            {index + 1}
          </Box>
          <Chip size="small" label={chip.label} color={chip.color} sx={chip.sx ?? severityChipSx(chip.color)} />
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4, flex: 1, minWidth: 0 }}>
            {brief.title}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ ...concernAccordionDetailsSx(), px: 0, pb: 0 }}>
        <AdjudicatorFindingBriefPanel brief={brief} index={index} embedded />
      </AccordionDetails>
    </Accordion>
  );
}

function ConflictDetail({ conflict }: { conflict: EmployerConflictInfo }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        mt: 1.25,
        p: 1.35,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.05 : 0.1),
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
        Employer information conflict
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
            Declared
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {conflict.declaredEmployer ?? '—'}
          </Typography>
          {conflict.declaredJobTitle ? (
            <Typography variant="caption" color="text.secondary">
              {conflict.declaredJobTitle}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
            {conflict.externalSourceLabel}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {conflict.externalEmployer ?? 'Different employer signal detected'}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

/** Adjudicator action briefs — one collapsible panel per issue. */
export function FindingEvidenceSummary({
  findings,
  conflict,
  detail = null,
  footprint = null,
  loading = false,
}: {
  findings: ReviewReason[];
  conflict: EmployerConflictInfo | null;
  detail?: EmployerVerificationDetail | null;
  footprint?: EmployerFootprintAdminViewDto | null;
  loading?: boolean;
}) {
  const nav = useEmployerEvidenceTourOptional();
  const briefs = resolveAdjudicatorFindingBriefs(findings, detail, footprint);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!briefs.length && !conflict) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        No verification briefs are available for this run yet.
      </Typography>
    );
  }

  const focusedId = nav?.focusedFindingId ?? null;

  const sectionNames = [
    FINDING_BRIEF_SECTION_LABEL.summary,
    FINDING_BRIEF_SECTION_LABEL.measures,
    FINDING_BRIEF_SECTION_LABEL.followup,
  ];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 0.85, lineHeight: 1.5 }}>
        {verificationBriefIntro(briefs.length)}
      </Typography>
      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 1.25 }}
        aria-label="Verification brief sections"
      >
        {sectionNames.map((name) => (
          <Typography
            key={name}
            component="span"
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              color: 'text.primary',
              opacity: 0.78,
              px: 0.85,
              py: 0.25,
              borderRadius: 999,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            {name}
          </Typography>
        ))}
      </Stack>
      <Stack spacing={1}>
        {briefs.map((brief, index) => (
          <FindingBriefAccordion
            key={brief.findingId}
            brief={brief}
            index={index}
            expanded={focusedId === brief.findingId}
            onToggle={(open) => {
              nav?.setFocusedFindingId(open ? brief.findingId : null);
            }}
          />
        ))}
      </Stack>
      {conflict ? <ConflictDetail conflict={conflict} /> : null}
    </Box>
  );
}
